import React from 'react';

interface DefaultTableProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Default table wrapper component with consistent styling
 * Use this as the base for all tables in the application
 */
export const DefaultTable: React.FC<DefaultTableProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`tw:overflow-x-auto ${className}`}>
      <table className="tw:min-w-full tw:bg-white tw:rounded-xl tw:overflow-hidden tw:shadow-md tw:border-0">
        {children}
      </table>
    </div>
  );
};

interface DefaultTableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const DefaultTableHeader: React.FC<DefaultTableHeaderProps> = ({
  children,
  className = '',
}) => {
  return (
    <thead className={`tw:bg-white tw:text-justify ${className}`}>
      {children}
    </thead>
  );
};

interface DefaultTableHeaderRowProps {
  children: React.ReactNode;
  className?: string;
}

export const DefaultTableHeaderRow: React.FC<DefaultTableHeaderRowProps> = ({
  children,
  className = '',
}) => {
  return <tr className={className}>{children}</tr>;
};

interface DefaultTableHeaderCellProps {
  children?: React.ReactNode;
  className?: string;
}

export const DefaultTableHeaderCell: React.FC<DefaultTableHeaderCellProps> = ({
  children,
  className = '',
}) => {
  return (
    <th
      className={`tw:px-6 tw:py-4 tw:text-sm tw:font-medium tw:text-gray-900 tw:border-b tw:border-gray-200 ${className}`}
    >
      {children}
    </th>
  );
};

interface DefaultTableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const DefaultTableBody: React.FC<DefaultTableBodyProps> = ({
  children,
  className = '',
}) => {
  return (
    <tbody
      className={`tw:divide-y tw:divide-gray-200 tw:bg-white tw:border-0 ${className}`}
    >
      {children}
    </tbody>
  );
};

interface DefaultTableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  selected?: boolean;
}

export const DefaultTableRow: React.FC<DefaultTableRowProps> = ({
  children,
  className = '',
  onClick,
  selected = false,
}) => {
  const baseClasses = 'tw:transition-colors';
  const hoverClasses = onClick ? 'tw:hover:bg-gray-50 tw:cursor-pointer' : '';
  const selectedClasses = selected ? 'tw:bg-blue-50' : '';
  const combinedClasses = `${baseClasses} ${hoverClasses} ${selectedClasses} ${className}`;

  return (
    <tr
      className={combinedClasses}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : {}}
    >
      {children}
    </tr>
  );
};

interface DefaultTableCellProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

export const DefaultTableCell: React.FC<DefaultTableCellProps> = ({
  children,
  className = '',
  onClick,
}) => {
  const cursorClass = onClick ? 'tw:cursor-pointer' : '';
  return (
    <td
      className={`tw:px-6 tw:py-4 tw:whitespace-nowrap tw:text-sm tw:text-gray-900 tw:border-0 ${cursorClass} ${className}`}
      onClick={onClick}
    >
      {children}
    </td>
  );
};
